import { useMemo } from 'react';
import { Link } from 'react-router-dom';

interface DashboardStatsProps {
  stats: {
    monthlyPosts: number;
    monthlyHours: number;
    weeklyPosts: number;
    totalPosts: number;
    totalHours: number;
    totalMaterials: number;
    totalVideos: number;
  };
  loading: boolean;
  layoutMode?: 'normal' | 'compact';
}

export default function DashboardStats({ stats, loading, layoutMode = 'normal' }: DashboardStatsProps) {
  const safeStats = useMemo(() => {
    const defaultStats = {
      monthlyPosts: 0,
      monthlyHours: 0,
      weeklyPosts: 0,
      totalPosts: 0,
      totalHours: 0,
      totalMaterials: 0,
      totalVideos: 0,
    };
    if (!stats || typeof stats !== 'object') return defaultStats;
    return { ...defaultStats, ...stats };
  }, [stats]);

  if (loading) {
    return (
      <section className="py-6 sm:py-8 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-3 sm:mb-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-200 dark:bg-gray-700 rounded-xl p-4 sm:p-5 h-20 sm:h-28"
              />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-100 dark:bg-gray-700 rounded-xl p-3 sm:p-4 h-14 sm:h-16"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  const statCards = [
    {
      label: '今月の投稿',
      value: safeStats.monthlyPosts,
      unit: '件',
      icon: 'ri-article-line',
      color: 'from-teal-500 to-emerald-600',
    },
    {
      label: '今月の学習時間',
      value: safeStats.monthlyHours,
      unit: '時間',
      icon: 'ri-time-line',
      color: 'from-teal-500 to-emerald-600',
    },
    {
      label: '直近7日の投稿',
      value: safeStats.weeklyPosts,
      unit: '件',
      icon: 'ri-calendar-check-line',
      color: 'from-teal-500 to-emerald-600',
    },
    {
      label: '累計学習時間',
      value: safeStats.totalHours,
      unit: '時間',
      icon: 'ri-timer-line',
      color: 'from-emerald-500 to-teal-600',
    },
  ];

  const summaryCards = [
    {
      label: '勉強日記',
      value: safeStats.totalPosts,
      unit: '件',
      icon: 'ri-book-open-line',
      link: '/diary',
    },
    {
      label: '勉強資料',
      value: safeStats.totalMaterials,
      unit: '件',
      icon: 'ri-file-text-line',
      link: '/materials',
    },
    {
      label: '講義動画',
      value: safeStats.totalVideos,
      unit: '本',
      icon: 'ri-video-line',
      link: '/videos',
    },
  ];

  return (
    <section className="py-6 sm:py-8 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* メイン統計カード: スマホ2列・PC4列 */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 ${layoutMode === 'compact' ? 'gap-3 mb-4' : 'gap-6 mb-8'}`}>
          {statCards.map((card) => (
            <div
              key={card.label}
              className={`bg-gradient-to-br ${card.color} rounded-xl p-3 sm:p-5 shadow-sm`}
            >
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-white/15 rounded-lg shrink-0">
                  <i className={`${card.icon} text-xs sm:text-base text-white/90`}></i>
                </div>
                <p className="text-[10px] sm:text-xs text-teal-100 leading-tight">{card.label}</p>
              </div>
              <p className="text-lg sm:text-2xl font-bold text-white leading-none">
                {card.value}
                <span className="text-xs sm:text-sm font-normal text-teal-100 ml-0.5 sm:ml-1">
                  {card.unit}
                </span>
              </p>
            </div>
          ))}
        </div>

        {/* サマリーリンクカード: スマホ3列（コンパクト）・PC3列 */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          {summaryCards.map((card) => (
            <Link
              key={card.label}
              to={card.link}
              className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-2.5 sm:p-4 flex items-center gap-2 sm:gap-3 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center bg-teal-50 dark:bg-teal-900/30 rounded-lg shrink-0">
                <i className={`${card.icon} text-sm sm:text-lg text-teal-600 dark:text-teal-400`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate leading-tight">
                  {card.label}
                </p>
                <p className="text-sm sm:text-lg font-bold text-gray-800 dark:text-white leading-tight">
                  {card.value}
                  <span className="text-[10px] sm:text-xs font-normal text-gray-500 dark:text-gray-400 ml-0.5">
                    {card.unit}
                  </span>
                </p>
              </div>
            </Link>
          ))}
        </div>

      </div>
    </section>
  );
}
